import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { BehaviorSubject, forkJoin, interval, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { CONFIG_OPTIONS, ConfigOptions } from '@venecia/tokens';

function isObject(v: any): v is Record<string, any> {
    return v && typeof v === 'object' && !Array.isArray(v);
}

function deepMerge(target: any, source: any) {
    for (const k of Object.keys(source)) {
        const sv = source[k];
        const tv = (target ?? {})[k];
        if (isObject(tv) && isObject(sv)) {
            target[k] = deepMerge({ ...tv }, sv);
        } else {
            target[k] = sv;
        }
    }
    return target;
}

function getByPath(obj: any, path: string, fallback?: any) {
    if (!path) {
        return obj ?? fallback;
    }
    return path
        .split('.')
        .reduce((acc, key) => (acc?.[key] ?? undefined), obj) ?? fallback;
}

@Injectable()
export class ConfigService {
    private readonly state$ = new BehaviorSubject<Record<string, any>>({});
    private loaded = false;

    constructor(
        private http: HttpClient,
        @Inject(CONFIG_OPTIONS) private options: ConfigOptions,
    ) {
        if (this.options.reloadIntervalMs && this.options.reloadIntervalMs > 0) {
            interval(this.options.reloadIntervalMs)
                .pipe(switchMap(() => this.loadAll()))
                .subscribe();
        }
    }

    /** Carga/recarga todas las URLs y hace merge profundo. */
    loadAll() {
        const urls = this.options.urls ?? [];
        if (!urls.length) {
            this.loaded = true;
            return of({});
        }

        const requests = urls.map(u =>
            this.http.get<Record<string, any>>(u)
                .pipe(
                    catchError(() => of({}))
                )
        );

        return forkJoin(requests)
            .pipe(
                map(parts =>
                    parts.reduce(
                        (acc, cur) => deepMerge(acc, cur),
                        {} as Record<string, any>
                    )
                ),
                tap(cfg => {
                    this.loaded = true;
                    this.state$.next(cfg);
                })
            );
    }

    /** Valor sincrónico por clave. */
    get<T = any>(path: string, fallback?: T): T {
        const effectiveFallback = (fallback === undefined ? (path as any as T) : fallback);
        return getByPath(this.state$.value, path, effectiveFallback);
    }

    select<T = any>(path?: string, fallback?: T) {
        return this.state$.asObservable().pipe(
            map(cfg => {
                if (!path) {
                    return (cfg ?? fallback) as T;
                }
                const effectiveFallback = (fallback === undefined ? (path as any as T) : fallback);
                return getByPath(cfg, path, effectiveFallback);
            })
        );
    }

    patch(partial: Record<string, any>) {
        const merged = deepMerge({ ...this.state$.value }, partial);
        this.state$.next(merged);
    }

    isLoaded() { return this.loaded; }
}
